import { useState, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { imgUrl } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import {
  Search, Ban, CheckCircle2, Shield, ShieldOff, Trash2, Plus, X,
  ChevronLeft, ChevronRight, UserX, UserCheck, Library, Loader2, TimerReset,
} from "lucide-react";

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "banned", label: "Banned" },
];

const SORT_OPTIONS = [
  { value: "created_at", label: "Joined" },
  { value: "last_login_at", label: "Last login" },
  { value: "last_active_at", label: "Last active" },
  { value: "username", label: "Username" },
  { value: "email", label: "Email" },
];

export default function AdminUsers() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);

  const params = useMemo(
    () => ({ q: q || undefined, status: status || undefined, sort, order, page, page_size: 20 }),
    [q, status, sort, order, page]
  );

  const { data, isLoading } = useQuery({
    queryKey: ["admin-users", params],
    queryFn: async () => (await api.get("/admin/users", { params })).data,
    keepPreviousData: true,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-users"] });

  const ban = async (u) => { await api.post(`/admin/users/${u.id}/ban`); toast.success("Banned"); refresh(); };
  const unban = async (u) => { await api.post(`/admin/users/${u.id}/unban`); toast.success("Unbanned"); refresh(); };
  const del = async (u) => {
    if (!window.confirm(`Delete ${u.username}? This removes the account and library.`)) return;
    await api.delete(`/admin/users/${u.id}`); toast.success("Deleted"); refresh();
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="p-8" data-testid="admin-users-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-brand-cyan mb-2">Members</div>
          <h1 className="font-heading text-3xl font-bold">{t("admin.user_mgmt")}</h1>
        </div>
        <div className="text-sm text-zinc-500">
          {data?.total ?? 0} <span className="text-zinc-600">total</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative">
          <Search className="w-4 h-4 absolute start-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search by username / email / country code"
            className="bg-[#080808] border border-white/10 rounded ps-9 pe-3 py-2 w-72 text-sm focus:border-brand outline-none"
            data-testid="users-search"
          />
        </div>
        <div className="flex gap-1 bg-[#080808] border border-white/10 rounded p-1">
          {STATUS_TABS.map((s) => (
            <button key={s.value} onClick={() => { setStatus(s.value); setPage(1); }}
              className={`px-3 py-1 rounded text-xs font-semibold ${status === s.value ? "bg-brand text-white" : "text-zinc-400 hover:text-white"}`}
              data-testid={`users-status-${s.value || "all"}`}
            >{s.label}</button>
          ))}
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="bg-[#080808] border border-white/10 rounded px-3 py-2 text-sm" data-testid="users-sort">
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>Sort: {o.label}</option>)}
        </select>
        <button onClick={() => setOrder(order === "desc" ? "asc" : "desc")} className="bg-[#080808] border border-white/10 rounded px-3 py-2 text-sm" data-testid="users-order">
          {order === "desc" ? "↓ Desc" : "↑ Asc"}
        </button>
      </div>

      {/* Table */}
      <div className="bg-[#0c0c0c] border border-white/5 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.02] text-xs uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-start px-4 py-3">User</th>
              <th className="text-start px-4 py-3">Country</th>
              <th className="text-start px-4 py-3">Games</th>
              <th className="text-start px-4 py-3">Last login</th>
              <th className="text-start px-4 py-3">Joined</th>
              <th className="text-start px-4 py-3">Status</th>
              <th className="text-start px-4 py-3">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline me-2" /> Loading…</td></tr>}
            {!isLoading && (data?.users || []).length === 0 && <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500">No users.</td></tr>}
            {(data?.users || []).map((u) => (
              <tr key={u.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                <td className="px-4 py-3">
                  <button onClick={() => setSelected(u)} className="text-start hover:underline">
                    <div className="font-medium">{u.username}</div>
                    <div className="text-xs text-zinc-500">{u.email}</div>
                  </button>
                </td>
                <td className="px-4 py-3 text-sm">
                  {u.country_flag ? <><span className="text-base me-1">{u.country_flag}</span>{u.country_code}</> : <span className="text-zinc-600">—</span>}
                </td>
                <td className="px-4 py-3 tabular-nums">{u.library_count}</td>
                <td className="px-4 py-3 text-xs text-zinc-400">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-3 text-xs text-zinc-400">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {u.is_banned
                      ? <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-red-500/15 text-red-400 border border-red-500/30">Banned</span>
                      : <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-brand-green/15 text-brand-green border border-brand-green/30">Active</span>}
                    {u.bypass_cooldown && <span className="px-2 py-0.5 rounded text-[10px] uppercase font-bold bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30">Bypass</span>}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => setSelected(u)} className="p-2 hover:bg-white/5 rounded" title="Manage library / Reset wait time" data-testid={`user-manage-${u.id}`}><Library className="w-4 h-4" /></button>
                    {u.is_banned
                      ? <button onClick={() => unban(u)} className="p-2 hover:bg-brand-green/10 rounded text-brand-green" title="Unban" data-testid={`user-unban-${u.id}`}><UserCheck className="w-4 h-4" /></button>
                      : <button onClick={() => ban(u)} className="p-2 hover:bg-red-500/10 rounded text-red-400" title="Ban" data-testid={`user-ban-${u.id}`}><UserX className="w-4 h-4" /></button>}
                    <button onClick={() => del(u)} className="p-2 hover:bg-red-500/10 rounded text-red-400" title="Delete" data-testid={`user-delete-${u.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-sm text-zinc-400">
        <div>Page {data?.page ?? 1} of {totalPages}</div>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 inline-flex items-center gap-1" data-testid="users-prev"><ChevronLeft className="w-4 h-4 rtl-flip" /> Prev</button>
          <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="px-3 py-1.5 rounded bg-white/5 hover:bg-white/10 disabled:opacity-30 inline-flex items-center gap-1" data-testid="users-next">Next <ChevronRight className="w-4 h-4 rtl-flip" /></button>
        </div>
      </div>

      {selected && <UserDetailModal user={selected} onClose={() => setSelected(null)} onRefresh={refresh} />}
    </div>
  );
}

function UserDetailModal({ user, onClose, onRefresh }) {
  const qc = useQueryClient();
  const [granting, setGranting] = useState(false);

  const { data: library = [] } = useQuery({
    queryKey: ["user-library", user.id],
    queryFn: async () => (await api.get(`/admin/users/${user.id}/library`)).data,
  });
  const { data: products = [] } = useQuery({
    queryKey: ["admin-products-min"],
    queryFn: async () => (await api.get("/products/admin/list")).data,
  });

  const grant = async (product_id) => {
    if (!product_id) return;
    setGranting(true);
    try {
      await api.post(`/admin/users/${user.id}/grant-game`, { product_id });
      toast.success("Game granted");
      qc.invalidateQueries({ queryKey: ["user-library", user.id] });
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setGranting(false);
    }
  };

  const remove = async (delivery_id) => {
    if (!window.confirm("Remove this game from user library?")) return;
    await api.delete(`/admin/users/${user.id}/library/${delivery_id}`);
    toast.success("Removed");
    qc.invalidateQueries({ queryKey: ["user-library", user.id] });
    onRefresh();
  };

  const resetWait = async (delivery_id) => {
    if (!window.confirm("Reset the 72-hour wait time for this game?")) return;
    try {
      await api.post(`/admin/users/${user.id}/library/${delivery_id}/reset-wait`);
      toast.success("Wait time reset. User can request a new code now.");
      qc.invalidateQueries({ queryKey: ["user-library", user.id] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const ownedIds = new Set(library.map((d) => d.product_id));
  const available = products.filter((p) => p.status === "available" && !ownedIds.has(p.id));

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0c0c0c] border border-white/10 rounded-xl max-w-2xl w-full my-8" data-testid="user-detail-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 sticky top-0 bg-[#0c0c0c]">
          <div>
            <h3 className="font-heading text-lg font-bold">{user.username} {user.country_flag && <span className="ms-2">{user.country_flag}</span>}</h3>
            <div className="text-xs text-zinc-500">{user.email}</div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Grant new game */}
          <div>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2 font-bold">Grant a game manually</div>
            <div className="flex gap-2">
              <select onChange={(e) => grant(e.target.value)} value="" disabled={granting} className="flex-1 bg-[#080808] border border-white/10 rounded px-3 py-2 text-sm" data-testid="grant-game-select">
                <option value="">— Pick a game to grant —</option>
                {available.map((p) => <option key={p.id} value={p.id}>{p.title} ({p.platform})</option>)}
              </select>
              {granting && <Loader2 className="w-4 h-4 animate-spin self-center" />}
            </div>
          </div>

          {/* Library */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-wider text-zinc-500 font-bold">Library ({library.length})</div>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {library.length === 0 && <div className="text-zinc-500 text-sm py-4 text-center">No games.</div>}
              {library.map((d) => (
                <div key={d.id} className="flex items-center gap-3 bg-[#080808] border border-white/5 rounded p-3" data-testid={`user-game-${d.id}`}>
                  {d.product_image && <img src={imgUrl(d.product_image)} alt="" className="w-12 h-12 rounded object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{d.product_title}</div>
                    <div className="text-xs text-zinc-500">{d.product_platform} · {new Date(d.created_at).toLocaleDateString()}</div>
                  </div>
                  <button onClick={() => resetWait(d.id)} className="p-2 hover:bg-brand/10 rounded text-brand-cyan" title="Reset Wait Time" data-testid={`reset-wait-${d.id}`}><TimerReset className="w-4 h-4" /></button>
                  <button onClick={() => remove(d.id)} className="p-2 hover:bg-red-500/10 rounded text-red-400" title="Remove" data-testid={`remove-game-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
